import Phaser from 'phaser';
import { getAuth, onAuthChange, updatePassword } from '../../store/auth.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from '../view/Button.js';
import { TextInputOverlay } from '../view/TextInputOverlay.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Set-new-password landing for the magic link in the reset email.
 * Mounted at /reset-password — main.ts boots this scene directly
 * when that route matches. Supabase's detectSessionInUrl: true on
 * the client picks up the access_token from the URL fragment and
 * signs the user into a "recovery-mode" session before we render.
 *
 * If the user lands here without a recovery session (link expired,
 * URL pasted directly, or someone bookmarked the page) we show an
 * error state rather than a broken-looking form — the next valid
 * action is to request a fresh link from ForgotPasswordScene.
 */
const PASSWORD_MIN = 8;

export class ResetPasswordScene extends Phaser.Scene {
  static readonly KEY = 'ResetPassword';

  private password = '';
  private submitting = false;

  private passwordInput: TextInputOverlay | undefined = undefined;
  private submitButton: Button | undefined = undefined;
  private cancelButton: Button | undefined = undefined;
  private statusText!: Phaser.GameObjects.Text;
  private unsubscribeAuth: (() => void) | undefined = undefined;

  constructor() {
    super(ResetPasswordScene.KEY);
  }

  preload(): void {
    this.load.image('resetPasswordLogo', '/assets/logo2.png');
  }

  init(): void {
    this.password = '';
    this.submitting = false;
    this.passwordInput = undefined;
    this.submitButton = undefined;
    this.cancelButton = undefined;
    this.unsubscribeAuth = undefined;
  }

  create(): void {
    this.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        COLORS.bgDeepInt
      )
      .setOrigin(0.5);

    this.drawLogo();

    // The Supabase SDK parses the URL fragment in the background
    // when detectSessionInUrl is on. There's a microtask between
    // module load and the auth listener firing — so the session may
    // not be loaded synchronously when create() runs. We render the
    // form optimistically and lean on the listener to bounce us out
    // if it turns out the session was never established.
    const auth = getAuth();
    if (auth.user) {
      this.drawForm();
    } else {
      this.drawInvalidLink();
    }

    // If the auth state arrives async (recovery-mode session lands
    // a tick after we mounted), tear down + redraw the form.
    this.unsubscribeAuth = onAuthChange((next) => {
      if (!next.user) return;
      // Only swap from invalid → form, never the reverse — a sign-out
      // during the flow shouldn't yank the form out from under the
      // user mid-submit.
      if (!this.passwordInput) {
        this.children.removeAll();
        this.add
          .rectangle(
            DESIGN_WIDTH / 2,
            DESIGN_HEIGHT / 2,
            DESIGN_WIDTH,
            DESIGN_HEIGHT,
            COLORS.bgDeepInt
          )
          .setOrigin(0.5);
        this.drawLogo();
        this.drawForm();
      }
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.passwordInput?.destroy();
      this.submitButton?.destroy();
      this.cancelButton?.destroy();
      this.unsubscribeAuth?.();
    });
  }

  private drawLogo(): void {
    const logo = this.add.image(DESIGN_WIDTH / 2, 0, 'resetPasswordLogo');
    const targetWidth = 540;
    const scale = Math.min(1, targetWidth / logo.width);
    logo.setScale(scale);
    const logoH = logo.height * scale;
    logo.setY(70 + logoH / 2);
  }

  private drawForm(): void {
    const cx = DESIGN_WIDTH / 2;

    this.add
      .text(cx, 320, 'Set a new password', {
        fontFamily: FONT_HEADER,
        fontSize: '36px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        390,
        `At least ${PASSWORD_MIN} characters.`,
        {
          fontFamily: FONT_MONO,
          fontSize: '15px',
          color: COLORS.textMuted,
        }
      )
      .setLetterSpacing(2)
      .setOrigin(0.5);

    const fieldWidth = 520;
    const fieldHeight = 64;

    this.add
      .text(cx - fieldWidth / 2, 528, 'NEW PASSWORD', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    this.passwordInput = new TextInputOverlay({
      scene: this,
      x: cx,
      y: 572,
      width: fieldWidth,
      height: fieldHeight,
      type: 'password',
      placeholder: 'At least 8 characters',
      autocomplete: 'new-password',
      maxLength: 128,
      fontSizeDesign: 26,
      onChange: (v) => {
        this.password = v;
      },
      onSubmit: () => {
        void this.handleSubmit();
      },
    });

    this.submitButton = new Button(this, {
      x: cx,
      y: 690,
      width: 360,
      height: 80,
      text: 'Save password',
      style: 'primary',
      onTap: () => {
        void this.handleSubmit();
      },
    });

    this.statusText = this.add
      .text(cx, 780, '', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: COLORS.accentRedHex,
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: 560 },
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);

    this.cancelButton = new Button(this, {
      x: cx,
      y: 1140,
      width: 240,
      height: 64,
      text: 'Cancel',
      onTap: () => {
        if (this.submitting) return;
        this.scene.start('Home');
      },
    });
  }

  private drawInvalidLink(): void {
    const cx = DESIGN_WIDTH / 2;

    this.add
      .text(cx, 380, 'Link expired or invalid', {
        fontFamily: FONT_HEADER,
        fontSize: '32px',
        color: COLORS.accentAmberHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        450,
        'This password-reset link is no longer valid. Request a new one to keep going.',
        {
          fontFamily: FONT_MONO,
          fontSize: '16px',
          color: COLORS.textPrimary,
          align: 'center',
          wordWrap: { width: 560 },
        }
      )
      .setLetterSpacing(1)
      .setOrigin(0.5);

    // Stub status so the field exists even in the invalid-link path —
    // simplifies the handleSubmit guard below.
    this.statusText = this.add.text(0, 0, '').setVisible(false);

    new Button(this, {
      x: cx,
      y: 640,
      width: 360,
      height: 80,
      text: 'Request a new link',
      style: 'primary',
      onTap: () => this.scene.start('ForgotPassword'),
    });

    new Button(this, {
      x: cx,
      y: 740,
      width: 240,
      height: 64,
      text: 'Back to home',
      onTap: () => this.scene.start('Home'),
    });
  }

  private async handleSubmit(): Promise<void> {
    if (this.submitting) return;

    if (this.password.length < PASSWORD_MIN) {
      this.setError(`Password must be at least ${PASSWORD_MIN} characters.`);
      this.passwordInput?.focus();
      return;
    }

    this.setSubmitting(true);
    const result = await updatePassword(this.password);
    if (!result.ok) {
      this.setError(result.error);
      return;
    }

    // Success — Supabase upgrades the recovery-mode session to a
    // normal one. Send the user to Home, which will route them
    // through the username gate if needed.
    this.scene.start('Home');
  }

  private setSubmitting(submitting: boolean): void {
    this.submitting = submitting;
    this.passwordInput?.setDisabled(submitting);
    if (submitting) {
      this.statusText.setColor(COLORS.accentAmberHex);
      this.statusText.setText('Saving…');
    }
  }

  private setError(msg: string): void {
    this.submitting = false;
    this.passwordInput?.setDisabled(false);
    this.statusText.setColor(COLORS.accentRedHex);
    this.statusText.setText(msg);
  }
}
